import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, qs } from "./client";
import { orgTodayStr } from "@/lib/org-time";
import type {
  AdminUser,
  AttendanceLogResponse,
  AttendanceLogRow,
  AttendanceRangeResponse,
  AttendanceSession,
  AppNotification,
  CompOffBalance,
  CompOffLedgerEntry,
  DocType,
  EmployeeDocument,
  EmployeeProfile,
  EmploymentType,
  Gender,
  Holiday,
  LeaveBalance,
  LeaveDay,
  LeaveMonthlySummary,
  LeaveMonthlySummaryMember,
  LeaveRequest,
  LeaveSummaryMember,
  LeaveType,
  OnboardingStatus,
  OrgCalendarResponse,
  Role,
  SettableBalanceType,
  SessionUser,
  Team,
  TeamMember,
  TodayAttendance,
  WfhDay,
  WfhEligibility,
  WfhRequest,
  WfhWeeklySummary,
  WorkMode,
} from "./types";

/** Query keys are namespaced by module so a mutation can invalidate a whole area. */
/**
 * How far back the per-person history dialog reads.
 *
 * Two years rather than "everything": long enough that nobody loses sight of a
 * day they care about (this system's records begin in 2026), short enough that
 * the request stops growing without bound as the years accumulate. Raise it if
 * anybody ever needs to look further back.
 */
const HISTORY_WINDOW_MONTHS = 24;

function historyWindow() {
  const end = orgTodayStr();
  const [y = 2026, m = 1, d = 1] = end.split("-").map(Number);
  const from = new Date(Date.UTC(y, m - 1 - HISTORY_WINDOW_MONTHS, d));
  return { start: from.toISOString().slice(0, 10), end };
}

export const keys = {
  me: ["auth", "me"] as const,
  adminUsers: ["admin", "users"] as const,
  adminTeams: ["admin", "teams"] as const,
  adminAttendance: (start: string, end: string) => ["admin", "attendance", start, end] as const,
  adminAttendanceUser: (id: number, start: string, end: string) =>
    ["admin", "attendance", "user", id, start, end] as const,
  adminLeave: ["admin", "leave"] as const,
  adminLeaveSummary: ["admin", "leave-summary"] as const,
  adminLeaveMonthlySummary: (month: string) => ["admin", "leave-monthly-summary", month] as const,
  adminCompOff: ["admin", "compoff-ledger"] as const,
  adminDocuments: (userId: number) => ["admin", "documents", userId] as const,

  managerTeam: ["manager", "team"] as const,
  managerAttendance: (start: string, end: string) => ["manager", "attendance", start, end] as const,
  managerAttendanceUser: (id: number, start: string, end: string) =>
    ["manager", "attendance", "user", id, start, end] as const,
  managerLeaveSummary: ["manager", "leave-summary"] as const,
  managerLeaveMonthlySummary: (month: string) => ["manager", "leave-monthly-summary", month] as const,
  managerCompOff: ["manager", "compoff-ledger"] as const,
  managerDocuments: (userId: number) => ["manager", "documents", userId] as const,

  attendanceToday: ["attendance", "today"] as const,
  attendanceMonth: (month: string) => ["attendance", "month", month] as const,
  attendanceRange: (start: string, end: string) => ["attendance", "range", start, end] as const,
  compOffBalance: ["attendance", "compoff-balance"] as const,
  compOffLedger: ["attendance", "compoff-ledger"] as const,

  myLeave: ["leave", "mine"] as const,
  myLeaveBalance: ["leave", "balance"] as const,
  myLeaveMonthlySummary: (month: string) => ["leave", "monthly-summary", month] as const,
  leaveTypes: ["leave", "types"] as const,
  teamLeave: ["leave", "team"] as const,

  // Work from home is its own namespace, not a corner of "leave" — a mutation
  // that invalidates one must not silently refetch the other, and the two
  // answer different questions.
  myWfh: ["wfh", "mine"] as const,
  wfhEligibility: ["wfh", "eligibility"] as const,
  teamWfh: ["wfh", "team"] as const,
  allWfh: ["wfh", "all"] as const,
  wfhWeeklySummary: ["wfh", "weekly-summary"] as const,

  notifications: ["notifications"] as const,

  holidays: ["holidays"] as const,
  orgCalendar: ["org-calendar"] as const,
  myDocuments: ["documents", "me"] as const,
  onboarding: ["onboarding", "status"] as const,
};

/**
 * Attendance is the one thing in here that changes while someone is watching it.
 *
 * The client defaults are deliberately quiet — 30s stale, no refetch on focus —
 * which is right for a directory or a leave policy but wrong for a live roster:
 * HR leaves the Attendance page open, people check in through the morning, and
 * the table they are looking at never moves. That reads as check-ins not
 * reaching HR at all, which is exactly the complaint it produced.
 *
 * Same reason the employee's own card opts in: it has to notice its own
 * check-in, and it has to roll over at midnight without a manual reload.
 */
const LIVE_ATTENDANCE = {
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
  staleTime: 15_000,
} as const;

/* -------------------------------------------------------------------------- */
/* HR & Admin                                                                  */
/* -------------------------------------------------------------------------- */

export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => api<{ users: AdminUser[] }>("/api/admin/users").then((r) => r.users),
    enabled,
  });
}

export function useTeams(enabled = true) {
  return useQuery({
    queryKey: keys.adminTeams,
    queryFn: () => api<{ teams: Team[] }>("/api/admin/teams").then((r) => r.teams),
    enabled,
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ team: Team }>("/api/admin/teams", { method: "POST", body: { name } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adminTeams }),
  });
}

export function useAdminAttendance(start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: keys.adminAttendance(start, end),
    queryFn: () => api<AttendanceRangeResponse>(`/api/admin/attendance${qs({ start, end })}`),
    enabled,
    ...LIVE_ATTENDANCE,
  });
}

/**
 * One person's attendance log, BOUNDED.
 *
 * Both handlers have always accepted `start` and `end` and defaulted to
 * '2000-01-01' → today when they are absent. Nothing sent them, so opening the
 * history dialog pulled every attendance row, every session and the entire
 * audit trail for that person, for all time — a payload that only ever grows.
 *
 * The window is deliberately wide (see HISTORY_WINDOW_MONTHS): this dialog is
 * where somebody goes to answer "what actually happened", and cutting it short
 * would trade one honesty problem for another.
 */
export function useAdminAttendanceUser(userId: number | null) {
  const { start, end } = historyWindow();
  return useQuery({
    queryKey: keys.adminAttendanceUser(userId ?? -1, start, end),
    queryFn: () =>
      api<AttendanceLogResponse>(`/api/admin/attendance/user/${userId}${qs({ start, end })}`),
    enabled: userId !== null,
  });
}

export function useAdminLeave(enabled = true) {
  return useQuery({
    queryKey: keys.adminLeave,
    queryFn: () => api<{ requests: LeaveRequest[] }>("/api/admin/leave").then((r) => r.requests),
    enabled,
  });
}

// POST /api/admin/leave/user/:id — HR & Admin recording a leave on behalf of
// any real account. Lands straight as approved (see utils/leaveAdmin.js).
export function useLogLeaveForUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: { userId: number } & LeaveInput) =>
      api<{ request: LeaveRequest }>(`/api/admin/leave/user/${userId}`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminLeave });
      qc.invalidateQueries({ queryKey: keys.adminLeaveSummary });
      qc.invalidateQueries({ queryKey: ["admin", "leave-monthly-summary"] });
      qc.invalidateQueries({ queryKey: keys.adminCompOff });
    },
  });
}

/**
 * HR states how many days of each type somebody has left for the year. A number
 * sets it, null hands it back to the policy calculation, a type left out is
 * untouched. Every balance screen reads these, so all of them are refreshed.
 */
export function useSetLeaveBalances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      balances,
      yearTotals,
      note,
    }: {
      userId: number;
      balances: Partial<Record<SettableBalanceType, number | null>>;
      /** The year's total per type, saved apart from the balance — see
       *  PUT /api/admin/leave-balance. */
      yearTotals: Partial<Record<SettableBalanceType, number | null>>;
      note: string;
    }) =>
      api<{ balance: LeaveBalance }>(`/api/admin/leave-balance/${userId}`, {
        method: "PUT",
        body: { balances, yearTotals, note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminLeaveSummary });
      qc.invalidateQueries({ queryKey: keys.managerLeaveSummary });
      qc.invalidateQueries({ queryKey: keys.myLeaveBalance });
    },
  });
}

/** Manager and Vyomi's own requests only — see api/types.ts LeaveRequest. */
export function useAdminDecideLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: "approved" | "rejected" }) =>
      api<{ request: LeaveRequest }>(`/api/admin/leave/${id}/decide`, {
        method: "POST",
        body: { status },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminLeave });
      qc.invalidateQueries({ queryKey: keys.adminLeaveSummary });
      // Approving a Comp Off request spends the requester's ledger, which is
      // exactly what the org-wide ledger's "Used" lines are.
      qc.invalidateQueries({ queryKey: keys.adminCompOff });
    },
  });
}

export function useAdminLeaveSummary(enabled = true) {
  return useQuery({
    queryKey: keys.adminLeaveSummary,
    queryFn: () => api<{ year: number; members: LeaveSummaryMember[] }>("/api/admin/leave-summary"),
    enabled,
  });
}

export function useAdminLeaveMonthlySummary(month: string, enabled = true) {
  return useQuery({
    queryKey: keys.adminLeaveMonthlySummary(month),
    queryFn: () =>
      api<{ month: string; members: LeaveMonthlySummaryMember[] }>(
        `/api/admin/leave-monthly-summary${qs({ month })}`
      ),
    enabled,
  });
}

export function useAdminCompOffLedger(enabled = true) {
  return useQuery({
    queryKey: keys.adminCompOff,
    queryFn: () =>
      api<{ ledger: CompOffLedgerEntry[] }>("/api/admin/compoff-ledger").then((r) => r.ledger),
    enabled,
  });
}

export function useAdminPersonDocuments(userId: number | null) {
  return useQuery({
    queryKey: keys.adminDocuments(userId ?? -1),
    queryFn: () =>
      api<{
        person: { id: number; name: string | null; email: string; role: string };
        profile: EmployeeProfile;
        documents: EmployeeDocument[];
      }>(`/api/admin/documents/${userId}`),
    enabled: userId !== null,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      name: string;
      designation: string;
      role: Role;
      teamId?: number | null;
      workMode: WorkMode;
      employmentType: EmploymentType;
      gender?: Gender;
      isPeopleFunctionApprover?: boolean;
    }) =>
      api<{ user: AdminUser; oneTimePassword: string }>("/api/admin/users", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adminUsers }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: number;
      email: string;
      name: string;
      designation: string;
      role: Role;
      teamId?: number | null;
      workMode: WorkMode;
      employmentType: EmploymentType;
      isPeopleFunctionApprover?: boolean;
    }) => api<{ user: AdminUser }>(`/api/admin/users/${id}`, { method: "PUT", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adminUsers }),
  });
}

// POST /api/admin/users/:id/reset-password — re-issues a one-time password for
// an account whose owner forgot theirs. The backend puts the account back into
// the forced-reset state, so their next login has to end with choosing a new
// password — the same journey a brand-new account goes through. Self-reset is
// rejected server-side; the UI hides the button on your own row for the same reason.
export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<{
        user: { id: number; email: string; name: string | null };
        oneTimePassword: string;
      }>(`/api/admin/users/${id}/reset-password`, { method: "POST" }),
    // The account's status flips to "First login pending" — the table needs to show it.
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adminUsers }),
  });
}

export function useSetUserGender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, gender }: { id: number; gender: Gender }) =>
      api<{ ok: true }>(`/api/admin/users/${id}/gender`, { method: "PUT", body: { gender } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adminUsers }),
  });
}

// PUT /api/admin/users/:id/dates — Vyomi's account only (enforced server-side);
// used from Org Calendar > Full Year > People to correct someone else's birthday
// or joining date.
export function useSetUserDates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dateOfBirth, dateJoined }: { id: number; dateOfBirth: string; dateJoined: string }) =>
      api<{ ok: true }>(`/api/admin/users/${id}/dates`, { method: "PUT", body: { dateOfBirth, dateJoined } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.adminUsers });
      qc.invalidateQueries({ queryKey: keys.orgCalendar });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Manager                                                                     */
/* -------------------------------------------------------------------------- */

export function useManagerTeam(enabled = true) {
  return useQuery({
    queryKey: keys.managerTeam,
    queryFn: () =>
      api<{ team: Team | null; members: TeamMember[]; coManagers: { id: number; name: string | null }[] }>(
        "/api/manager/team"
      ),
    enabled,
  });
}

export function useManagerAttendance(start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: keys.managerAttendance(start, end),
    queryFn: () => api<AttendanceRangeResponse>(`/api/manager/attendance${qs({ start, end })}`),
    enabled,
    ...LIVE_ATTENDANCE,
  });
}

/** The manager's view of the same log, bounded the same way. */
export function useManagerAttendanceUser(userId: number | null) {
  const { start, end } = historyWindow();
  return useQuery({
    queryKey: keys.managerAttendanceUser(userId ?? -1, start, end),
    queryFn: () =>
      api<AttendanceLogResponse>(`/api/manager/attendance/user/${userId}${qs({ start, end })}`),
    enabled: userId !== null,
  });
}

export function useManagerLeaveSummary(enabled = true) {
  return useQuery({
    queryKey: keys.managerLeaveSummary,
    queryFn: () => api<{ year: number; members: LeaveSummaryMember[] }>("/api/manager/leave-summary"),
    enabled,
  });
}

export function useManagerLeaveMonthlySummary(month: string, enabled = true) {
  return useQuery({
    queryKey: keys.managerLeaveMonthlySummary(month),
    queryFn: () =>
      api<{ month: string; members: LeaveMonthlySummaryMember[] }>(
        `/api/manager/leave-monthly-summary${qs({ month })}`
      ),
    enabled,
  });
}

export function useManagerCompOffLedger(enabled = true) {
  return useQuery({
    queryKey: keys.managerCompOff,
    queryFn: () =>
      api<{ ledger: CompOffLedgerEntry[] }>("/api/manager/compoff-ledger").then((r) => r.ledger),
    enabled,
  });
}

export function useManagerPersonDocuments(userId: number | null) {
  return useQuery({
    queryKey: keys.managerDocuments(userId ?? -1),
    queryFn: () =>
      api<{ person: { id: number; name: string | null; email: string }; documents: EmployeeDocument[] }>(
        `/api/manager/documents/${userId}`,
      ),
    enabled: userId !== null,
  });
}

export function useTeamLeave(enabled = true) {
  return useQuery({
    queryKey: keys.teamLeave,
    queryFn: () => api<{ requests: LeaveRequest[] }>("/api/leave/team").then((r) => r.requests),
    enabled,
  });
}

export function useDecideLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: "approved" | "rejected" }) =>
      api<{ request: LeaveRequest }>(`/api/leave/${id}/decide`, { method: "POST", body: { status } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.teamLeave });
      qc.invalidateQueries({ queryKey: keys.managerLeaveSummary });
      // Approving a Comp Off request spends that employee's ledger, which is
      // exactly what the team ledger's "Used" lines are.
      qc.invalidateQueries({ queryKey: keys.managerCompOff });
    },
  });
}

// POST /api/manager/leave/user/:id — a manager recording a leave on behalf of
// a real employee on their own team. Lands straight as approved.
export function useLogTeamLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: { userId: number } & LeaveInput) =>
      api<{ request: LeaveRequest }>(`/api/manager/leave/user/${userId}`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.teamLeave });
      qc.invalidateQueries({ queryKey: keys.managerLeaveSummary });
      qc.invalidateQueries({ queryKey: ["manager", "leave-monthly-summary"] });
      qc.invalidateQueries({ queryKey: keys.managerCompOff });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Attendance (self)                                                           */
/* -------------------------------------------------------------------------- */

export function useAttendanceToday() {
  return useQuery({
    queryKey: keys.attendanceToday,
    queryFn: () => api<TodayAttendance>("/api/attendance/today"),
    ...LIVE_ATTENDANCE,
  });
}

export function useAttendanceMonth(month: string) {
  return useQuery({
    queryKey: keys.attendanceMonth(month),
    queryFn: () =>
      api<{
        records: AttendanceLogRow[];
        workMode: WorkMode;
        sessionsByDate: Record<string, AttendanceSession[]>;
        leave?: LeaveDay[];
        wfh?: WfhDay[];
      }>(`/api/attendance/month${qs({ month })}`),
  });
}

export function useAttendanceRange(start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: keys.attendanceRange(start, end),
    queryFn: () => api<AttendanceRangeResponse>(`/api/attendance/range${qs({ start, end })}`),
    enabled,
  });
}

export function useCompOffBalance() {
  return useQuery({
    queryKey: keys.compOffBalance,
    queryFn: () => api<CompOffBalance>("/api/attendance/compoff-balance"),
  });
}

export function useCompOffLedger() {
  return useQuery({
    queryKey: keys.compOffLedger,
    queryFn: () =>
      api<{ ledger: CompOffLedgerEntry[] }>("/api/attendance/compoff-ledger").then((r) => r.ledger),
  });
}

/**
 * Check-in / check-out / remote mark all refresh the same set of views.
 *
 * Leave is invalidated as well as attendance, and it is not incidental: working
 * a weekly off or a holiday credits the comp-off ledger, and the ledger is what
 * the Comp Off line of the leave balance is computed from. Without this the
 * balance screen went on showing the pre-credit number until something else
 * happened to refetch it.
 */
function useAttendanceMutation<TInput>(fn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: keys.compOffBalance });
      qc.invalidateQueries({ queryKey: keys.compOffLedger });
      qc.invalidateQueries({ queryKey: keys.myLeaveBalance });
    },
  });
}

export function useCheckIn() {
  return useAttendanceMutation<void>(() => api("/api/attendance/check-in", { method: "POST" }));
}

export function useCheckOut() {
  return useAttendanceMutation<void>(() => api("/api/attendance/check-out", { method: "POST" }));
}

/**
 * A remote day is declared, not clocked: one press says full, half, or not
 * working. Replaces the old active/inactive pair — see the backend route.
 */
export function useMarkRemote() {
  return useAttendanceMutation<{ status: "full" | "half" | "off" }>((body) =>
    api("/api/attendance/mark", { method: "POST", body }),
  );
}

export function useEnterPastAttendance() {
  return useAttendanceMutation<{
    date: string;
    mode: "onsite" | "remote";
    /** Onsite only — a remote day is declared rather than timed. */
    checkIn?: string;
    checkOut?: string;
    /** Remote only. */
    status?: "full" | "half" | "off";
  }>((body) => api("/api/attendance/enter", { method: "POST", body }));
}

/**
 * HR and manager corrections. Both scopes expose the same four operations,
 * differing only in the path prefix; the backend is what enforces that a manager
 * can only reach their own team.
 *
 * Invalidates the scoped attendance views AND the per-user log, since every one
 * of these changes both the day's totals and its audit trail.
 *
 * The leave summary goes with them: correcting a rest day re-runs the comp-off
 * credit for it (maybeCreditCompOff can take a credit back down as well as put
 * one up), and that credit is the Comp Off line of every balance on the leave
 * screen. Refreshing the ledger without the balance left the two showing
 * different numbers side by side.
 */
function useCorrectionMutation<TInput>(scope: "admin" | "manager", fn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [scope, "attendance"] });
      qc.invalidateQueries({ queryKey: [scope, "compoff-ledger"] });
      qc.invalidateQueries({ queryKey: [scope, "leave-summary"] });
    },
  });
}

/** Adds a session to a day. */
export function useAddSession(scope: "admin" | "manager") {
  return useCorrectionMutation<{
    userId: number;
    date: string;
    mode: "onsite" | "remote";
    checkIn: string;
    checkOut: string;
  }>(scope, ({ userId, ...body }) =>
    api(`/api/${scope}/attendance/user/${userId}/sessions`, { method: "POST", body }),
  );
}

/** Rewrites one session's times. `eventId` is the session's own id from the log. */
export function useEditSession(scope: "admin" | "manager") {
  return useCorrectionMutation<{
    userId: number;
    eventId: number;
    checkIn: string;
    checkOut: string;
  }>(scope, ({ userId, eventId, ...body }) =>
    api(`/api/${scope}/attendance/user/${userId}/sessions/${eventId}`, { method: "PUT", body }),
  );
}

/** Removes a session. Its events survive as voided rows in the trail. */
export function useRemoveSession(scope: "admin" | "manager") {
  return useCorrectionMutation<{ userId: number; eventId: number }>(scope, ({ userId, eventId }) =>
    api(`/api/${scope}/attendance/user/${userId}/sessions/${eventId}`, { method: "DELETE" }),
  );
}

/**
 * Replaces a whole day — one session, or a declared remote day off. Kept
 * alongside the session operations because a day off has no session to edit, and
 * because starting a tangled day over is sometimes cleaner than fixing it
 * piece by piece.
 */
export function useReplaceDay(scope: "admin" | "manager") {
  return useCorrectionMutation<{
    userId: number;
    date: string;
    mode: "onsite" | "remote";
    checkIn?: string | null;
    checkOut?: string | null;
    status?: "inactive" | null;
  }>(scope, ({ userId, ...body }) =>
    api(`/api/${scope}/attendance/user/${userId}`, { method: "PUT", body }),
  );
}

/* -------------------------------------------------------------------------- */
/* Leave (self)                                                                */
/* -------------------------------------------------------------------------- */

export function useMyLeave() {
  return useQuery({
    queryKey: keys.myLeave,
    queryFn: () => api<{ requests: LeaveRequest[] }>("/api/leave/mine").then((r) => r.requests),
  });
}

export function useMyLeaveBalance() {
  return useQuery({
    queryKey: keys.myLeaveBalance,
    queryFn: () =>
      api<{ year: number; balance: LeaveBalance; compOff: CompOffBalance }>("/api/leave/balance"),
  });
}

export function useMyLeaveMonthlySummary(month: string) {
  return useQuery({
    queryKey: keys.myLeaveMonthlySummary(month),
    queryFn: () => api<LeaveMonthlySummary>(`/api/leave/monthly-summary${qs({ month })}`),
  });
}

export function useLeaveTypes() {
  return useQuery({
    queryKey: keys.leaveTypes,
    queryFn: () => api<{ types: LeaveType[] }>("/api/leave/types").then((r) => r.types),
  });
}

/**
 * The comp-off views go with the leave ones, in the other direction from
 * useAttendanceMutation: a Comp Off request is what SPENDS the ledger, so
 * applying for one, editing it, or pulling it back changes the earned/used/
 * available figures the attendance screen shows.
 */
function useLeaveMutation<TInput>(fn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myLeave });
      qc.invalidateQueries({ queryKey: keys.myLeaveBalance });
      qc.invalidateQueries({ queryKey: keys.compOffBalance });
      qc.invalidateQueries({ queryKey: keys.compOffLedger });
    },
  });
}

export interface LeaveInput {
  startDate: string;
  endDate: string;
  reason: string;
  leaveType: LeaveType;
}

export function useApplyLeave() {
  return useLeaveMutation<LeaveInput>((body) => api("/api/leave/apply", { method: "POST", body }));
}

export function useEditLeave() {
  return useLeaveMutation<LeaveInput & { id: number }>(({ id, ...body }) =>
    api(`/api/leave/${id}`, { method: "PATCH", body }),
  );
}

/** Deletes a request that is still PENDING. Approved leave uses withdrawApproved. */
export function useWithdrawLeave() {
  return useLeaveMutation<number>((id) => api(`/api/leave/${id}`, { method: "DELETE" }));
}

/**
 * Hands back leave that was already APPROVED — the whole thing, or one day of it
 * when `date` is given. Immediate: the days return to the balance straight away
 * and the approver is notified rather than asked.
 *
 * Invalidates attendance as well as leave, because a withdrawn day changes what
 * the tracker says about it — that agreement is the entire point.
 */
export function useWithdrawApprovedLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, date }: { id: number; date?: string }) =>
      api<{ fullyWithdrawn: boolean; withdrawnDates: string[] }>(`/api/leave/${id}/withdraw`, {
        method: "POST",
        body: date ? { date } : {},
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myLeave });
      qc.invalidateQueries({ queryKey: keys.myLeaveBalance });
      qc.invalidateQueries({ queryKey: ["leave"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: keys.notifications });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Work from home                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Every WFH mutation refreshes the same three things, and it is worth saying why
 * each one is in the list:
 *
 *   ["wfh"]        the lists and the charts — a decision changes both.
 *   ["attendance"] an approved day changes what checking in will record, and the
 *                  team grid paints the day. Leaving this out is how the tracker
 *                  ends up disagreeing with the request that caused it.
 *   notifications  a request and a decision both write one.
 */
function useWfhMutation<TInput>(fn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wfh"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["manager", "attendance"] });
      qc.invalidateQueries({ queryKey: ["admin", "attendance"] });
      qc.invalidateQueries({ queryKey: keys.notifications });
    },
  });
}

export function useMyWfh() {
  return useQuery({
    queryKey: keys.myWfh,
    queryFn: () => api<{ requests: WfhRequest[] }>("/api/wfh/mine").then((r) => r.requests),
  });
}

/** Whether this account can raise a request at all — remote accounts cannot. */
export function useWfhEligibility() {
  return useQuery({
    queryKey: keys.wfhEligibility,
    queryFn: () => api<WfhEligibility>("/api/wfh/eligibility"),
  });
}

export function useTeamWfh(enabled = true) {
  return useQuery({
    queryKey: keys.teamWfh,
    queryFn: () => api<{ requests: WfhRequest[] }>("/api/wfh/team").then((r) => r.requests),
    enabled,
  });
}

export function useAllWfh(enabled = true) {
  return useQuery({
    queryKey: keys.allWfh,
    queryFn: () => api<{ requests: WfhRequest[] }>("/api/wfh/all").then((r) => r.requests),
    enabled,
  });
}

/** Scoped server-side: a manager's own team, or org-wide for HR & Admin. */
export function useWfhWeeklySummary(enabled = true) {
  return useQuery({
    queryKey: keys.wfhWeeklySummary,
    queryFn: () => api<WfhWeeklySummary>("/api/wfh/weekly-summary"),
    enabled,
  });
}

export interface WfhInput {
  startDate: string;
  endDate: string;
  reason: string;
}

export function useApplyWfh() {
  return useWfhMutation<WfhInput>((body) => api("/api/wfh/apply", { method: "POST", body }));
}

/** Approve or decline. The backend decides whether this caller may. */
export function useDecideWfh() {
  return useWfhMutation<{ id: number; status: "approved" | "rejected" }>(({ id, status }) =>
    api(`/api/wfh/${id}/decide`, { method: "POST", body: { status } }),
  );
}

/** Cancels a request that is still PENDING. An approved one is the approver's. */
export function useCancelWfh() {
  return useWfhMutation<number>((id) => api(`/api/wfh/${id}`, { method: "DELETE" }));
}

/* -------------------------------------------------------------------------- */
/* Notifications                                                               */
/* -------------------------------------------------------------------------- */

export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications,
    queryFn: () => api<{ notifications: AppNotification[]; unread: number }>("/api/notifications"),
    // Something another person did (a decision, a withdrawal) can arrive at any
    // time, so this is the one query in the app that is worth re-asking on its
    // own. A minute is slow enough to be free and fast enough to be useful.
    refetchInterval: 60_000,
  });
}

function useNotificationMutation<TInput>(fn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications }),
  });
}

export function useMarkNotificationRead() {
  return useNotificationMutation<number>((id) =>
    api(`/api/notifications/${id}/read`, { method: "POST" }),
  );
}

export function useMarkAllNotificationsRead() {
  return useNotificationMutation<void>(() =>
    api("/api/notifications/read-all", { method: "POST" }),
  );
}

export function useDismissNotification() {
  return useNotificationMutation<number>((id) =>
    api(`/api/notifications/${id}`, { method: "DELETE" }),
  );
}

export function useDismissAllNotifications() {
  return useNotificationMutation<void>(() => api("/api/notifications", { method: "DELETE" }));
}

/* -------------------------------------------------------------------------- */
/* Shared: holidays, org calendar, own documents, onboarding                   */
/* -------------------------------------------------------------------------- */

export function useHolidays(options?: Partial<UseQueryOptions<Holiday[]>>) {
  return useQuery({
    queryKey: keys.holidays,
    queryFn: () => api<{ holidays: Holiday[] }>("/api/holidays").then((r) => r.holidays),
    ...options,
  });
}

export function useAddHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; name: string }) =>
      api<{ holiday: Holiday }>("/api/holidays", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.holidays }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/holidays/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.holidays }),
  });
}

export function useOrgCalendar() {
  return useQuery({
    queryKey: keys.orgCalendar,
    queryFn: () => api<OrgCalendarResponse>("/api/org-calendar"),
  });
}

export function useMyDocuments() {
  return useQuery({
    queryKey: keys.myDocuments,
    queryFn: () =>
      api<{
        profile: EmployeeProfile;
        documents: EmployeeDocument[];
        dateOfBirth: string | null;
        dateJoined: string | null;
      }>("/api/documents/me"),
  });
}

// PUT /api/documents/me/dates — self-service edit of birthday and work
// anniversary from My Documents, any role, any time after onboarding.
export function useSaveMyDates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dateOfBirth: string; dateJoined: string }) =>
      api<{ ok: true }>("/api/documents/me/dates", { method: "PUT", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myDocuments });
      qc.invalidateQueries({ queryKey: keys.onboarding });
      qc.invalidateQueries({ queryKey: keys.orgCalendar });
    },
  });
}

export interface ProfileInput {
  accountNumber?: string;
  accountHolderName?: string;
  bankName?: string;
  ifscCode?: string;
  currentAddress?: string;
  permanentAddress?: string;
  sameAsCurrent?: boolean;
  bloodGroup?: string;
  gender?: Gender;
  aadhaarNumber?: string;
  panNumber?: string;
  emergencyContact1Name?: string;
  emergencyContact1Number?: string;
  emergencyContact2Name?: string;
  emergencyContact2Number?: string;
}

export function useSaveMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfileInput) =>
      api<{ profile: EmployeeProfile }>("/api/documents/me/profile", { method: "PUT", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myDocuments });
      qc.invalidateQueries({ queryKey: keys.onboarding });
    },
  });
}

export function useUploadMyDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ docType, file }: { docType: DocType; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      return api(`/api/documents/me/upload/${docType}`, { method: "POST", formData });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myDocuments });
      qc.invalidateQueries({ queryKey: keys.onboarding });
    },
  });
}

export function useAdminUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, docType, file }: { userId: number; docType: DocType; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      return api(`/api/admin/documents/${userId}/upload/${docType}`, { method: "POST", formData });
    },
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: keys.adminDocuments(variables.userId) }),
  });
}

export function useSaveAdminProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: ProfileInput & { userId: number }) =>
      api<{ profile: EmployeeProfile }>(`/api/admin/documents/${userId}/profile`, {
        method: "PUT",
        body,
      }),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: keys.adminDocuments(variables.userId) }),
  });
}

export function useOnboardingStatus(enabled = true) {
  return useQuery({
    queryKey: keys.onboarding,
    queryFn: () => api<OnboardingStatus>("/api/onboarding/status"),
    enabled,
  });
}

export function useSaveOnboardingDates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dateOfBirth: string; dateJoined: string }) =>
      api("/api/onboarding/dates", { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.onboarding }),
  });
}

export function useCompleteOnboarding() {
  return useMutation({
    mutationFn: () => api<{ ok: true; token: string }>("/api/onboarding/complete", { method: "POST" }),
  });
}

/* -------------------------------------------------------------------------- */
/* Account settings                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The signed-in user, re-read from the server.
 *
 * The stored copy is written at login and never refreshed, so anything HR
 * changes about an account afterwards — most visibly a switch to remote working
 * — stayed invisible on that person's own screens until they logged out. This
 * is the read that keeps the local copy honest; useSyncSession wires it in.
 */
export function useMe(enabled = true) {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api<{ user: SessionUser }>("/api/auth/me"),
    enabled,
  });
}

export function useUpdateMe() {
  return useMutation({
    mutationFn: (body: { name: string; designation: string }) =>
      api<{ user: SessionUser }>("/api/auth/me", { method: "PUT", body }),
  });
}

/** Self-service email change, any role. Returns a fresh token: the old one
 *  carries the previous address in its signed payload. */
export function useChangeEmail() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; email: string }) =>
      api<{ ok: true; email: string; token: string }>("/api/auth/email", {
        method: "PUT",
        body,
      }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api<{ ok: true; token: string }>("/api/auth/change-password", { method: "POST", body }),
  });
}

export function useUpdateWorkMode() {
  return useMutation({
    mutationFn: (workMode: WorkMode) =>
      api<{ ok: true; workMode: WorkMode }>("/api/auth/work-mode", {
        method: "PUT",
        body: { workMode },
      }),
  });
}

export function useSetMyGender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gender: Gender) =>
      api<{ profile: EmployeeProfile }>("/api/documents/me/gender", {
        method: "PUT",
        body: { gender },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myDocuments });
      qc.invalidateQueries({ queryKey: keys.leaveTypes });
      qc.invalidateQueries({ queryKey: keys.myLeaveBalance });
    },
  });
}
